import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { LuArrowLeft, LuArrowRight } from 'react-icons/lu';

import {
	VideoProjectCreationProvider,
	useVideoProjectCreation,
	DEFAULT_CONFIDENCE,
} from '@contexts/VideoProjectCreation.context';
import { useNewProject } from '@contexts/NewProject.context';
import { extractVideoMetadata, formatFileSize } from '@utils/video/helpers';
import { DEFAULT_MODEL_OPTION } from '@config/videoDetectionModelConfig';

import VideoFlowShell from '@routes/userRoutes/projects/videoProject/flow/VideoFlowShell';
import StepRail from '@routes/userRoutes/projects/videoProject/flow/StepRail';
import { FilmStrip } from '@routes/userRoutes/projects/videoProject/flow/Strips';
import RangeOverlay from '@routes/userRoutes/projects/videoProject/flow/RangeOverlay';
import OverlayPlayer from '@routes/userRoutes/projects/videoProject/components/overlay/OverlayPlayer';
import { formatDuration } from '@routes/userRoutes/projects/videoProject/flow/ProjectCards';
import {
	FlowPanel,
	PanelTitle,
	SectionTitle,
	Muted,
	Small,
	Pills,
	Pill,
	KeyValues,
	FlowButton,
	ButtonRow,
	ProgressBar,
	SpreadLine,
	Note,
	Rule,
	Foot,
} from '@routes/userRoutes/projects/videoProject/flow/primitives';
import FlowDropZone from './flow/FlowDropZone';
import {
	ProjectDetailsCard,
	CountModeCards,
	ModelSelect,
	SensitivityField,
	TrimField,
	modeOf,
} from './flow/WizardParts';

/**
 * New video project in the three-column flow (docs/video-flow-design/VIDEO_FLOW_DESIGN.html, screens 1-3):
 * select the video, configure counting on the video itself, upload. The project logic (create, multipart upload,
 * start the job, archive on failure) stays in VideoProjectCreation.context.
 * Wizard stages are 3 (select) · 4 (configure) · 5 (upload) · 6 (processing did not start).
 */

const STAGE_STEP = { 3: 0, 4: 1, 5: 2, 6: 3 };
const ASSUMED_FPS = 30; // the browser does not expose a video's frame rate; only used to map the line editor

const toLine = d => (d ? { start: d.start_point, end: d.end_point } : null);
const fromLine = l => ({
	start_point: [Math.round(l.start[0]), Math.round(l.start[1])],
	end_point: [Math.round(l.end[0]), Math.round(l.end[1])],
});

/** OverlayPlayer needs a track index to map the line editor onto the video; an empty one does for a raw video. */
const emptyIndex = meta => {
	const n = Math.max(1, Math.round((meta?.duration || 1) * ASSUMED_FPS));
	return {
		fps: ASSUMED_FPS,
		width: meta.width,
		height: meta.height,
		nFrames: n,
		tracks: [],
		perFrame: new Int32Array(n + 1),
		trackOf: [],
		pointOf: [],
	};
};

/** Reads duration and size from the local file once, the same way the old player did. */
const useLocalMetadata = (videoURL, videoFile, setVideoMetadata) => {
	useEffect(() => {
		if (!videoURL) return undefined;
		const v = document.createElement('video');
		v.preload = 'metadata';
		v.muted = true;
		const file = {
			fileSize: videoFile?.size || 0,
			fileName: videoFile?.name || '',
			fileType: videoFile?.type || '',
		};
		const ok = () => setVideoMetadata({ ...extractVideoMetadata(v), ...file });
		const fail = () =>
			setVideoMetadata({ duration: 0, width: 0, height: 0, aspectRatio: 0, ...file });
		v.addEventListener('loadedmetadata', ok);
		v.addEventListener('error', fail);
		v.src = videoURL;
		return () => {
			v.removeEventListener('loadedmetadata', ok);
			v.removeEventListener('error', fail);
			v.removeAttribute('src');
			v.load();
		};
	}, [videoURL, videoFile, setVideoMetadata]);
};

/** Upload speed and time left from the progress percentage. */
const useUploadRate = (progress, totalBytes) => {
	const first = useRef(null);
	const [rate, setRate] = useState(null);
	useEffect(() => {
		if (!progress) {
			first.current = null;
			return;
		}
		const now = Date.now();
		if (!first.current) first.current = { t: now, p: progress };
		const dt = (now - first.current.t) / 1000;
		if (dt > 2) setRate(((progress - first.current.p) / 100) * (totalBytes / dt));
	}, [progress, totalBytes]);
	if (!rate) return null;
	const left = ((100 - progress) / 100) * totalBytes;
	return { mbps: rate / 1e6, secondsLeft: left / rate };
};

const minutes = s => (s < 90 ? `${Math.max(1, Math.round(s))} s` : `${Math.round(s / 60)} min`);

const BEFORE_YOU_START = [
	['Camera straight down', 'Tilted views count fewer animals'],
	['Steady altitude', '40–80 m works well for reindeer'],
	['Trim before upload', 'Pick the part to analyse in the next step'],
];

const VideoProjectWizardContent = () => {
	const navigate = useNavigate();
	const { setMode } = useNewProject();
	const { watch } = useFormContext();
	const projectName = (watch('projectName') || '').trim();
	const {
		currentStage,
		nextStage,
		previousStage,
		canProceedFromStage,
		videoFile,
		setVideoFile,
		videoURL,
		videoMetadata,
		setVideoMetadata,
		configuration,
		setConfiguration,
		uploadProgress,
		isUploading,
		isProcessing,
		createProjectAndUpload,
		retryProcessing,
		projectUuid,
		error: uploadError,
	} = useVideoProjectCreation();

	useLocalMetadata(videoURL, videoFile, setVideoMetadata);
	const playerRef = useRef(null);
	const duration = videoMetadata?.duration || 0;
	const canPreview = duration > 0 && videoMetadata?.width > 0;
	const index = useMemo(
		() => (canPreview ? emptyIndex(videoMetadata) : null),
		[canPreview, videoMetadata]
	);
	const mode = modeOf(configuration);
	const range = configuration.timeRange;

	// default range: the whole clip
	useEffect(() => {
		if (duration && !configuration.timeRange)
			setConfiguration(c => ({
				...c,
				timeRange: { start_seconds: 0, end_seconds: Math.round(duration * 100) / 100 },
			}));
	}, [duration, configuration.timeRange, setConfiguration]);

	const chooseMode = useCallback(
		m => {
			setConfiguration(c => ({
				...c,
				countingScenario: m.scenario,
				countingMethod: m.method,
				// a line across the middle to start from; drag it or its ends, or drag on the video for a new one
				virtualLineDefinition:
					m.method === 'line_based'
						? c.virtualLineDefinition ||
							(videoMetadata?.height
								? fromLine({
										start: [
											videoMetadata.width * 0.03,
											videoMetadata.height / 2,
										],
										end: [videoMetadata.width * 0.97, videoMetadata.height / 2],
									})
								: null)
						: null,
			}));
		},
		[setConfiguration, videoMetadata]
	);

	const analysed = range ? range.end_seconds - range.start_seconds : duration;
	const estimate = analysed ? Math.max(2, Math.round((analysed * 5) / 60) + 2) : null;
	const rate = useUploadRate(uploadProgress, videoFile?.size || 0);
	const step = STAGE_STEP[currentStage] ?? 0;

	const startUpload = async () => {
		nextStage();
		await createProjectAndUpload();
	};

	const retry = async () => {
		if (await retryProcessing()) navigate(`/video-projects/${projectUuid}`, { replace: true });
	};

	const summaries = {
		0: videoFile?.name,
		1:
			step > 1 && mode
				? [
						mode.title.split(' · ')[0],
						range &&
							`${formatDuration(range.start_seconds)}–${formatDuration(range.end_seconds)}`,
					]
						.filter(Boolean)
						.join(' · ')
				: null,
		2:
			currentStage === 5
				? `${Math.floor(uploadProgress)} %${rate ? ` · ~${minutes(rate.secondsLeft)} left` : ''}`
				: null,
		3: currentStage === 6 ? 'Did not start' : null,
	};

	const left = (
		<>
			<StepRail
				current={step}
				currentState={currentStage === 6 ? 'stopped' : 'current'}
				summaries={summaries}
			/>
			<ProjectDetailsCard
				disabled={currentStage >= 5}
				onChangeType={currentStage === 3 ? () => setMode(null) : null}
			/>
		</>
	);

	// ---- centre
	let centre;
	if (!videoFile) centre = <FlowDropZone onFile={setVideoFile} />;
	else {
		const editing = currentStage === 4 && mode?.method === 'line_based' && index;
		const line =
			mode?.method === 'line_based' ? toLine(configuration.virtualLineDefinition) : null;
		centre = (
			<>
				<OverlayPlayer
					ref={playerRef}
					src={videoURL}
					index={index}
					line={index ? line : null}
					lineEditable={!!editing}
					onLineChange={l =>
						setConfiguration(c => ({ ...c, virtualLineDefinition: fromLine(l) }))
					}
					showDetections={false}
					aspect={
						videoMetadata?.aspectRatio
							? `${videoMetadata.width} / ${videoMetadata.height}`
							: undefined
					}
				>
					{currentStage >= 5 && (
						<div
							style={{
								position: 'absolute',
								inset: 0,
								background: 'rgba(8, 10, 8, 0.55)',
								display: 'grid',
								placeItems: 'center',
							}}
						>
							<FlowPanel
								as="div"
								style={{
									width: 380,
									maxWidth: '90%',
									padding: '1.375rem 1.625rem',
								}}
							>
								<PanelTitle>
									{uploadProgress >= 100
										? 'Starting the analysis…'
										: 'Uploading video'}
								</PanelTitle>
								<ProgressBar value={uploadProgress} label="Upload progress" />
								<SpreadLine>
									<span>
										{formatFileSize(
											((videoFile?.size || 0) * uploadProgress) / 100
										)}{' '}
										of {formatFileSize(videoFile?.size || 0)}
									</span>
									<span>
										{rate
											? `${rate.mbps.toFixed(0)} MB/s · ~${minutes(rate.secondsLeft)} left`
											: ''}
									</span>
								</SpreadLine>
								<Small>
									Keep this tab open until the upload finishes. The analysis
									starts by itself.
								</Small>
							</FlowPanel>
						</div>
					)}
				</OverlayPlayer>
				{currentStage === 4 && duration ? (
					<FilmStrip
						src={videoURL}
						title="Part to analyse · trim"
						right={
							range
								? `Drag the handles · ${formatDuration(range.start_seconds)} – ${formatDuration(range.end_seconds)} (${Math.round(analysed)} s)`
								: null
						}
					>
						{range && (
							<RangeOverlay
								duration={duration}
								start={range.start_seconds}
								end={range.end_seconds}
								onChange={({ start, end }) => {
									setConfiguration(c => ({
										...c,
										timeRange: { start_seconds: start, end_seconds: end },
									}));
									playerRef.current?.seekTo(
										start !== range.start_seconds ? start : end
									);
								}}
							/>
						)}
					</FilmStrip>
				) : canPreview ? (
					<FilmStrip src={videoURL} right={`0:00 – ${formatDuration(duration)}`} />
				) : null}
			</>
		);
	}

	// ---- right
	let right;
	if (currentStage === 3 && !videoFile)
		right = (
			<FlowPanel $fill>
				<PanelTitle>Before you start</PanelTitle>
				<Muted>
					The model counts the animals it can see from above. A few things make a big
					difference:
				</Muted>
				{BEFORE_YOU_START.map(([t, h]) => (
					<div
						key={t}
						style={{
							border: '1px solid #343834',
							borderRadius: 12,
							padding: '0.7rem 0.75rem',
						}}
					>
						<SectionTitle>{t}</SectionTitle>
						<Small>{h}</Small>
					</div>
				))}
				<Foot>
					Your video stays private to your organisation.
					<br />
					Processing takes roughly 5× the length of the part you analyse.
				</Foot>
			</FlowPanel>
		);
	else if (currentStage === 3)
		right = (
			<FlowPanel $fill>
				<PanelTitle>{videoFile.name}</PanelTitle>
				<Pills>
					<Pill>
						{videoMetadata ? (canPreview ? 'Ready' : 'No preview') : 'Reading…'}
					</Pill>
					{videoMetadata?.height >= 2160 && <Pill $variant="ghost">4K</Pill>}
				</Pills>
				<KeyValues
					items={[
						['Duration', duration ? formatDuration(duration) : null],
						[
							'Resolution',
							videoMetadata?.width
								? `${videoMetadata.width} × ${videoMetadata.height}`
								: null,
						],
						['Size', formatFileSize(videoFile.size)],
						['Type', videoFile.type || null],
					]}
				/>
				{videoMetadata && !canPreview && (
					<Note>
						This browser cannot play the video, so you cannot draw a counting line. It
						can still be uploaded and counted.
					</Note>
				)}
				<Rule />
				<Muted>
					{projectName
						? 'Next you choose how to count and which part of the video to analyse.'
						: 'Give the project a name (left) to continue.'}
				</Muted>
				<ButtonRow>
					<FlowButton
						type="button"
						$variant="secondary"
						onClick={() => setVideoFile(null)}
					>
						Change
					</FlowButton>
					<FlowButton
						type="button"
						onClick={nextStage}
						disabled={!canProceedFromStage(3) || !projectName || !videoMetadata}
					>
						Continue <LuArrowRight />
					</FlowButton>
				</ButtonRow>
			</FlowPanel>
		);
	else if (currentStage === 4)
		right = (
			<FlowPanel $fill>
				<PanelTitle>How should we count?</PanelTitle>
				<CountModeCards value={mode?.id} onChange={chooseMode} lineDisabled={!canPreview} />
				{mode?.method === 'line_based' && (
					<Small>
						Drag the line or its ends on the video, or drag across the video to draw a
						new one. Animals are counted when they cross it.
					</Small>
				)}
				<TrimField
					range={range}
					duration={duration}
					getNow={() => playerRef.current?.video()?.currentTime || 0}
					onChange={r => {
						const moved =
							r.start_seconds !== range.start_seconds
								? r.start_seconds
								: r.end_seconds;
						setConfiguration(c => ({ ...c, timeRange: r }));
						playerRef.current?.seekTo(moved);
					}}
				/>
				<ModelSelect
					value={configuration.modelOption || DEFAULT_MODEL_OPTION}
					onChange={o => setConfiguration(c => ({ ...c, modelOption: o }))}
				/>
				<SensitivityField
					value={configuration.confidenceThreshold ?? DEFAULT_CONFIDENCE}
					onChange={v => setConfiguration(c => ({ ...c, confidenceThreshold: v }))}
				/>
				{estimate && (
					<Small>
						Estimated processing time:{' '}
						<b style={{ color: '#b4b9b4' }}>about {estimate} minutes</b> for{' '}
						{Math.round(analysed)} s of video.
					</Small>
				)}
				<ButtonRow>
					<FlowButton type="button" $variant="secondary" onClick={previousStage}>
						<LuArrowLeft /> Back
					</FlowButton>
					<FlowButton
						type="button"
						onClick={startUpload}
						disabled={!canProceedFromStage(4) || !projectName}
					>
						Upload &amp; analyse
					</FlowButton>
				</ButtonRow>
			</FlowPanel>
		);
	else {
		const settings = (
			<KeyValues
				items={[
					['Counting', mode?.title],
					[
						'Period',
						range
							? `${formatDuration(range.start_seconds)} – ${formatDuration(range.end_seconds)}`
							: 'Whole video',
					],
					[
						'Model',
						(configuration.modelOption || DEFAULT_MODEL_OPTION)?.label?.replace(
							/\s*\(.*\)$/,
							''
						),
					],
					[
						'Sensitivity',
						`${Math.round((configuration.confidenceThreshold ?? DEFAULT_CONFIDENCE) * 100)} %`,
					],
					['Est. time', estimate ? `~${estimate} min` : null],
				]}
			/>
		);
		right =
			currentStage === 6 ? (
				<FlowPanel $fill>
					<PanelTitle>The analysis did not start</PanelTitle>
					<Note $tone="error">
						The video was uploaded, but the analysis could not be started.
					</Note>
					{settings}
					<FlowButton type="button" onClick={retry} disabled={isProcessing}>
						{isProcessing ? 'Starting…' : 'Try again'}
					</FlowButton>
					{projectUuid && (
						<FlowButton
							type="button"
							$variant="secondary"
							onClick={() => navigate(`/video-projects/${projectUuid}`)}
						>
							Open the project
						</FlowButton>
					)}
				</FlowPanel>
			) : uploadError && !isUploading ? (
				<FlowPanel $fill>
					<PanelTitle>The upload did not finish</PanelTitle>
					<Note $tone="error">
						Nothing was saved. Check the connection and try again.
					</Note>
					{settings}
					<ButtonRow>
						<FlowButton type="button" $variant="secondary" onClick={previousStage}>
							<LuArrowLeft /> Back
						</FlowButton>
						<FlowButton type="button" onClick={createProjectAndUpload}>
							Try again
						</FlowButton>
					</ButtonRow>
				</FlowPanel>
			) : (
				<FlowPanel $fill>
					<PanelTitle>Your settings</PanelTitle>
					<Pills>{mode && <Pill>{mode.title}</Pill>}</Pills>
					{settings}
					<Rule />
					<Muted>
						{isUploading
							? 'The analysis starts as soon as the upload is done; this page then moves to the project. If all GPU machines are busy, the job waits in the queue and you can leave the page once the upload is done.'
							: 'Preparing the upload…'}
					</Muted>
				</FlowPanel>
			);
	}

	return (
		<VideoFlowShell
			crumbs={[
				{ title: 'Projects', link: '/projects' },
				{ title: 'New video project' },
				{ title: projectName || 'Untitled' },
			]}
			left={left}
			centre={centre}
			right={right}
		/>
	);
};

const VideoProjectWizard = () => {
	const { watch } = useFormContext();
	const projectInfo = {
		title: watch('projectName'),
		description: watch('projectDescription'),
		tags: watch('projectTags')?.map(tag => tag.value) || [],
	};
	return (
		<VideoProjectCreationProvider projectInfo={projectInfo}>
			<VideoProjectWizardContent />
		</VideoProjectCreationProvider>
	);
};

export default VideoProjectWizard;
